import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Awareness } from 'y-protocols/awareness';
import type { AwarenessState } from './types';

// Subscribes to a y-protocols Awareness instance and returns the map of
// remote peers (own clientID excluded) plus a setter for local state.
//
// Decoupled from any specific provider — accepts anything that exposes
// `awareness`. Works the same against y-websocket's WebsocketProvider and
// our backendless P2pProvider.
//
// y-protocols/awareness emits 'change' on add/update/remove with a
// { added, updated, removed } payload of clientIDs. We rebuild the peer
// snapshot via getStates() each tick — for ~10 peers this is trivial.
export interface AwarenessHost {
  awareness: Awareness;
}

export function useAwareness(provider: AwarenessHost) {
  const awareness = provider.awareness;
  const localClientID = awareness.clientID;

  const [peers, setPeers] = useState<Map<number, AwarenessState>>(new Map());

  useEffect(() => {
    const refresh = () => {
      const next = new Map<number, AwarenessState>();
      awareness.getStates().forEach((state: unknown, clientID: number) => {
        if (clientID === localClientID) return;
        const cast = state as AwarenessState | null | undefined;
        if (!cast || !cast.user) return;
        next.set(clientID, cast);
      });
      setPeers(next);
    };
    awareness.on('change', refresh);
    refresh();
    return () => {
      awareness.off('change', refresh);
    };
  }, [awareness, localClientID]);

  const setLocalState = useCallback(
    (state: AwarenessState) => {
      awareness.setLocalState(state);
    },
    [awareness],
  );

  const patchLocalField = useCallback(
    <K extends keyof AwarenessState>(key: K, value: AwarenessState[K]) => {
      awareness.setLocalStateField(key as string, value as never);
    },
    [awareness],
  );

  // Useful: peers grouped by what they have selected, so we can paint
  // selection rings on those shapes in their cursor color.
  const peerSelections = useMemo(() => {
    const map = new Map<string, { clientID: number; colorIndex: number; name: string }[]>();
    peers.forEach((state, clientID) => {
      for (const shapeId of state.selection ?? []) {
        const list = map.get(shapeId) ?? [];
        list.push({
          clientID,
          colorIndex: state.user.colorIndex,
          name: state.user.name,
        });
        map.set(shapeId, list);
      }
    });
    return map;
  }, [peers]);

  return { peers, peerSelections, setLocalState, patchLocalField };
}
